<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { usePermissions } from 'dashboard/composables/usePermissions';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const { can } = usePermissions();
// Silenciar/Reactivar restringido al permiso 'mute_conversation' (Victor 2026-05-26).
const canMute = computed(() => can('mute_conversation'));

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const actionMenuItems = computed(() => {
  const items = [];

  if (canMute.value) {
    if (!currentChat.value.muted) {
      items.push({
        icon: 'i-lucide-volume-off',
        label: t('CONTACT_PANEL.MUTE_CONTACT'),
        action: 'mute',
        value: 'mute',
      });
    } else {
      items.push({
        icon: 'i-lucide-volume-1',
        label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
        action: 'unmute',
        value: 'unmute',
      });
    }
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

// Listeners de Cmd+K: solo se registran si el agente tiene permiso.
if (canMute.value) {
  emitter.on(CMD_MUTE_CONVERSATION, mute);
  emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
}
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  if (canMute.value) {
    emitter.off(CMD_MUTE_CONVERSATION, mute);
    emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  }
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <!-- ⋮ "Más acciones" oculto por pedido de Victor 2026-05-18: chocaba con
         el ⚙ de perfil/config. Solo queda "Resolver" + el ⚙ a su lado. -->
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
